import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  CircularProgress,
  Stack,
  IconButton,
  Card,
  CardContent,
  Tooltip,
  Collapse
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Calendar, Link, PencilSimple, CaretDown, CaretRight } from '@phosphor-icons/react';
import { getStaffMemberDisplayPhoto } from '../../../utils/avatarConstants';
import { loadCrmPreferences, saveCrmPreferencesMerge } from '../../../utils/crmPreferences';
import { normalizeCrmStages } from '../../../utils/crmStagesOrder';
import { getLeadSourceLabel, mergeLeadSources } from '../../../utils/crmLeadSources';

const collapsedSlugsToRecord = (slugs) =>
  (Array.isArray(slugs) ? slugs : []).reduce((acc, slug) => {
    acc[slug] = true;
    return acc;
  }, {});

export const CRM_GROUP_BY = {
  STAGE: 'stage',
  SOURCE: 'source',
  PRIORITY: 'priority',
  ASSIGNEE: 'assignee',
  TAG: 'tag'
};

export const CRM_LAYOUT = {
  LIST: 'list',
  KANBAN: 'kanban'
};

/** Viewport height minus CRM page chrome (title, toolbar, padding) — Kanban board fills the rest */
const KANBAN_BOARD_HEIGHT = 'calc(100dvh - 15rem)';

const getPriorityChip = (priority) => {
  switch (priority) {
    case 'hot':
      return { label: 'High', color: 'error' };
    case 'warm':
      return { label: 'Medium', color: 'warning' };
    case 'cold':
      return { label: 'Low', color: 'info' };
    case 'high':
      return { label: 'High', color: 'error' };
    case 'medium':
      return { label: 'Medium', color: 'warning' };
    case 'low':
      return { label: 'Low', color: 'info' };
    default:
      return { label: priority || 'Low', color: 'default' };
  }
};

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' });
};

const normPriorityKey = (p) => {
  if (!p) return 'low';
  const x = String(p).toLowerCase();
  if (x === 'hot' || x === 'high') return 'high';
  if (x === 'warm' || x === 'medium') return 'medium';
  if (x === 'cold' || x === 'low') return 'low';
  return 'low';
};

const PRIORITY_SECTIONS = [
  { key: 'high', label: 'High' },
  { key: 'medium', label: 'Medium' },
  { key: 'low', label: 'Low' }
];

const formatSourceSectionLabel = (key, sourceOptions = []) => {
  if (key === 'none') return 'No source';
  if (!key || key === 'none') return 'No source';
  return getLeadSourceLabel(key, sourceOptions);
};

const buildSourceSections = (leadList, sourceOptions = []) => {
  const buckets = {};
  leadList.forEach((lead) => {
    const key = lead.leadSource ? String(lead.leadSource) : 'none';
    if (!buckets[key]) buckets[key] = [];
    buckets[key].push(lead);
  });
  const order = ['website', 'instagram', 'referral', 'chat', 'manual', 'other', 'none'];
  const keys = Object.keys(buckets);
  keys.sort((a, b) => {
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  return keys.map((key) => ({
    key,
    label: formatSourceSectionLabel(key, sourceOptions),
    leads: buckets[key]
  }));
};

const buildPrioritySections = (leadList) => {
  const buckets = { high: [], medium: [], low: [] };
  leadList.forEach((lead) => {
    const k = normPriorityKey(lead.priority);
    buckets[k].push(lead);
  });
  return PRIORITY_SECTIONS.map(({ key, label }) => ({
    key,
    label,
    leads: buckets[key]
  }));
};

const getAssigneeKey = (lead) => {
  const id = lead.assignedTo?._id || lead.assignedTo;
  if (!id) return 'unassigned';
  return String(id);
};

const getAssigneeDisplayLabel = (lead) => {
  if (!lead.assignedTo) return 'Unassigned';
  const u = lead.assignedTo.user;
  if (u && (u.firstName || u.lastName)) {
    return `${u.firstName || ''} ${u.lastName || ''}`.trim();
  }
  if (typeof lead.assignedTo === 'object' && lead.assignedTo.name) {
    return lead.assignedTo.name;
  }
  return 'Team member';
};

const buildAssigneeSections = (leadList) => {
  const buckets = new Map();
  leadList.forEach((lead) => {
    const key = getAssigneeKey(lead);
    if (!buckets.has(key)) {
      buckets.set(key, {
        leads: [],
        label: key === 'unassigned' ? 'Unassigned' : getAssigneeDisplayLabel(lead)
      });
    }
    buckets.get(key).leads.push(lead);
  });
  const entries = Array.from(buckets.entries());
  entries.sort((a, b) => {
    if (a[0] === 'unassigned') return 1;
    if (b[0] === 'unassigned') return -1;
    return a[1].label.localeCompare(b[1].label);
  });
  return entries.map(([key, { leads: ls, label }]) => ({ key, label, leads: ls }));
};

const getLeadTags = (lead) =>
  (Array.isArray(lead?.tags) ? lead.tags : [])
    .map((tag) => String(tag || '').trim())
    .filter(Boolean);

const buildTagSections = (leadList) => {
  const buckets = new Map();
  leadList.forEach((lead) => {
    const tags = getLeadTags(lead);
    if (!tags.length) {
      if (!buckets.has('no-tags')) {
        buckets.set('no-tags', { label: 'No tags', leads: [] });
      }
      buckets.get('no-tags').leads.push(lead);
      return;
    }

    tags.forEach((tag) => {
      const key = tag.toLowerCase();
      if (!buckets.has(key)) {
        buckets.set(key, { label: tag, leads: [] });
      }
      buckets.get(key).leads.push(lead);
    });
  });

  const entries = Array.from(buckets.entries()).sort((a, b) => {
    if (a[0] === 'no-tags') return 1;
    if (b[0] === 'no-tags') return -1;
    return a[1].label.localeCompare(b[1].label);
  });

  return entries.map(([key, data]) => ({ key, label: data.label, leads: data.leads }));
};

const getAssignedUserName = (assignedUser) => {
  if (!assignedUser) return 'Assigned user';
  const fullName = `${assignedUser.firstName || ''} ${assignedUser.lastName || ''}`.trim();
  return fullName || assignedUser.name || assignedUser.email || 'Assigned user';
};

function LeadCells({ lead, priorityMeta, dragSnapshot, sourceOptions }) {
  const leadTags = getLeadTags(lead);
  const visibleTags = leadTags.slice(0, 2);
  const hiddenTagCount = Math.max(leadTags.length - visibleTags.length, 0);

  return (
    <>
      <Box
        sx={{
          display: { xs: 'none', md: 'grid' },
          gridTemplateColumns: '2fr 2fr 1.2fr 1.2fr 1.2fr 1.2fr',
          alignItems: 'center',
          gap: 0,
          px: 1,
          py: 1.25,
          backgroundColor: '#fff',
          borderRadius: 1,
          transition: 'all 0.2s ease',
          zIndex: dragSnapshot?.isDragging ? 1000 : 1,
          '&:hover': { backgroundColor: dragSnapshot?.isDragging ? '#fff' : 'grey.50' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          {lead.wedding && (
            <Tooltip title="Linked to an Idotoo wedding" enterTouchDelay={0}>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  color: 'success.main',
                  flexShrink: 0,
                  lineHeight: 0,
                  mr: 1
                }}
                aria-label="Linked to an Idotoo wedding"
              >
                <Link size={20} weight="bold" />
              </Box>
            </Tooltip>
          )}
          <Typography noWrap sx={{ flex: 1, minWidth: 0 }}>
            {lead.name}
          </Typography>
        </Box>
        <Typography noWrap color="text.secondary">
          {lead.email}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          {lead.leadSource && (
            <Chip size="small" label={getLeadSourceLabel(lead.leadSource, sourceOptions)} variant="outlined" color="primary" />
          )}
          {visibleTags.map((tag, index) => (
            <Chip key={`${lead._id}-tag-${tag}-${index}`} size="small" label={tag} variant="outlined" />
          ))}
          {hiddenTagCount > 0 && <Chip size="small" label={`+${hiddenTagCount}`} variant="outlined" />}
        </Box>
        <Box>
          <Chip size="small" label={priorityMeta.label} color={priorityMeta.color} variant="filled" />
        </Box>
        <Box>
          {lead.weddingDate && (
            <Chip size="small" icon={<Calendar size={14} />} label={formatDate(lead.weddingDate)} variant="outlined" />
          )}
        </Box>
        <Box>
          {lead.assignedTo?.user && (
            <Tooltip title={getAssignedUserName(lead.assignedTo.user)}>
              <Avatar
                  src={getStaffMemberDisplayPhoto(lead.assignedTo)}
                alt={getAssignedUserName(lead.assignedTo.user)}
                sx={{ width: 36, height: 36 }}
              >
                {(lead.assignedTo.user.firstName || 'U')[0]}
              </Avatar>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Card sx={{ display: { xs: 'block', md: 'none' }, boxShadow: dragSnapshot?.isDragging ? 4 : 1, '&:hover': { boxShadow: 2 } }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, minWidth: 0 }}>
            {lead.wedding && (
              <Tooltip title="Linked to an Idotoo wedding" enterTouchDelay={0}>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    color: 'success.main',
                    flexShrink: 0,
                    lineHeight: 0,
                    mr: 1
                  }}
                  aria-label="Linked to an Idotoo wedding"
                >
                  <Link size={22} weight="bold" />
                </Box>
              </Tooltip>
            )}
            <Typography variant="subtitle1" fontWeight="600" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {lead.name}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 1 }}>
            {lead.email}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {lead.leadSource && <Chip size="small" label={getLeadSourceLabel(lead.leadSource, sourceOptions)} variant="outlined" color="primary" />}
            {visibleTags.map((tag, index) => (
              <Chip key={`${lead._id}-mobile-tag-${tag}-${index}`} size="small" label={tag} variant="outlined" />
            ))}
            {hiddenTagCount > 0 && <Chip size="small" label={`+${hiddenTagCount}`} variant="outlined" />}
            <Chip size="small" label={priorityMeta.label} color={priorityMeta.color} variant="filled" />
            {lead.weddingDate && (
              <Chip size="small" icon={<Calendar size={14} />} label={formatDate(lead.weddingDate)} variant="outlined" />
            )}
            {lead.assignedTo?.user && (
              <Tooltip title={getAssignedUserName(lead.assignedTo.user)}>
                <Avatar
                  src={getStaffMemberDisplayPhoto(lead.assignedTo)}
                  alt={getAssignedUserName(lead.assignedTo.user)}
                  sx={{ width: 30, height: 30 }}
                >
                  {(lead.assignedTo.user.firstName || 'U')[0]}
                </Avatar>
              </Tooltip>
            )}
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}

function KanbanLeadCard({ lead, priorityMeta, dragSnapshot }) {
  const leadTags = getLeadTags(lead);
  const visibleTags = leadTags.slice(0, 2);
  const hiddenTagCount = Math.max(leadTags.length - visibleTags.length, 0);

  return (
    <Card
      sx={{
        borderRadius: 0.5,
        boxShadow: dragSnapshot?.isDragging ? 4 : 1,
        '&:hover': { boxShadow: 2 },
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, minWidth: 0 }}>
          {lead.wedding && (
            <Tooltip title="Linked to an Idotoo wedding" enterTouchDelay={0}>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  color: 'success.main',
                  flexShrink: 0,
                  lineHeight: 0,
                  mr: 1
                }}
                aria-label="Linked to an Idotoo wedding"
              >
                <Link size={20} weight="bold" />
              </Box>
            </Tooltip>
          )}
          <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
            {lead.name}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mb: 1 }}>
          {lead.email}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1 }}>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ gap: 0.5, flex: 1 }}>
            <Chip size="small" label={priorityMeta.label} color={priorityMeta.color} variant="filled" />
            {visibleTags.map((tag, index) => (
              <Chip key={`${lead._id}-kanban-tag-${tag}-${index}`} size="small" label={tag} variant="outlined" />
            ))}
            {hiddenTagCount > 0 && <Chip size="small" label={`+${hiddenTagCount}`} variant="outlined" />}
            {lead.weddingDate && (
              <Chip size="small" icon={<Calendar size={14} />} label={formatDate(lead.weddingDate)} variant="outlined" />
            )}
          </Stack>
          {lead.assignedTo?.user && (
            <Tooltip title={getAssignedUserName(lead.assignedTo.user)}>
              <Avatar
                src={getStaffMemberDisplayPhoto(lead.assignedTo)}
                alt={getAssignedUserName(lead.assignedTo.user)}
                sx={{ width: 30, height: 30, flexShrink: 0 }}
              >
                {(lead.assignedTo.user.firstName || 'U')[0]}
              </Avatar>
            </Tooltip>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

const CRMListView = ({
  stages = [],
  leads = [],
  leadSources = [],
  onStageChange,
  onLeadClick,
  loading,
  onEditStage,
  viewingArchived = false,
  groupBy = CRM_GROUP_BY.STAGE,
  layout = CRM_LAYOUT.LIST
}) => {
  const allStages = normalizeCrmStages(stages).filter((stage) => !stage.isArchived);
  const orderedStages = viewingArchived
    ? allStages.filter((s) => s.slug === 'archived')
    : allStages.filter((s) => s.slug !== 'archived');

  const groupedByStage = useMemo(() => {
    const groupedLeads = orderedStages.reduce((acc, stage) => {
      acc[stage.slug] = [];
      return acc;
    }, {});

    leads.forEach((lead) => {
      const slug = lead.stageSlug || lead.stage || 'enquired';
      const targetStage = groupedLeads[slug] ? slug : 'enquired';
      groupedLeads[targetStage] = groupedLeads[targetStage] || [];
      groupedLeads[targetStage].push(lead);
    });
    return groupedLeads;
  }, [leads, orderedStages]);

  const sourceOptions = useMemo(() => mergeLeadSources(leadSources), [leadSources]);

  const alternateSections = useMemo(() => {
    if (groupBy === CRM_GROUP_BY.SOURCE) return buildSourceSections(leads, sourceOptions);
    if (groupBy === CRM_GROUP_BY.PRIORITY) return buildPrioritySections(leads);
    if (groupBy === CRM_GROUP_BY.ASSIGNEE) return buildAssigneeSections(leads);
    if (groupBy === CRM_GROUP_BY.TAG) return buildTagSections(leads);
    return [];
  }, [groupBy, leads, sourceOptions]);

  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;
    onStageChange(draggableId, destination.droppableId);
  };

  const [collapsedStages, setCollapsedStages] = useState(() =>
    collapsedSlugsToRecord(loadCrmPreferences().collapsedStageSlugs)
  );
  const toggleStageCollapsed = (slug) => {
    setCollapsedStages((prev) => {
      const next = { ...prev, [slug]: !prev[slug] };
      const collapsedSlugs = Object.keys(next).filter((k) => next[k]);
      saveCrmPreferencesMerge({ collapsedStageSlugs: collapsedSlugs });
      return next;
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="320px">
        <CircularProgress />
      </Box>
    );
  }

  const tableHeader = (
    <Box
      sx={{
        display: { xs: 'none', md: 'grid' },
        gridTemplateColumns: '2fr 2fr 1.2fr 1.2fr 1.2fr 1.2fr',
        gap: 0,
        px: 2,
        py: 1.25,
        backgroundColor: '#fff',
        fontWeight: 600,
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'grey.200',
        borderTopLeftRadius: 6,
        borderTopRightRadius: 6
      }}
    >
      <Typography variant="body2" noWrap>
        Name
      </Typography>
      <Typography variant="body2" noWrap>
        Email
      </Typography>
      <Typography variant="body2" noWrap>
        Source / Tags
      </Typography>
      <Typography variant="body2" noWrap>
        Priority
      </Typography>
      <Typography variant="body2" noWrap>
        Wedding Date
      </Typography>
      <Typography variant="body2" noWrap>
        Assigned
      </Typography>
    </Box>
  );

  const renderLeadBlock = (lead, { draggable, index }) => {
    const priorityMeta = getPriorityChip(lead.priority);
    const onRowClick = () => onLeadClick?.(lead);

    if (draggable) {
      return (
        <Draggable key={lead._id} draggableId={lead._id} index={index}>
          {(dragProvided, dragSnapshot) => {
            const style = dragProvided.draggableProps.style || {};
            const transform =
              style.transform && typeof style.transform === 'string'
                ? style.transform.replace(/translate\(([^,]+),/, 'translate(0px,')
                : style.transform;
            return (
              <Box
                ref={dragProvided.innerRef}
                {...dragProvided.draggableProps}
                {...dragProvided.dragHandleProps}
                onClick={onRowClick}
                style={{
                  ...style,
                  transform: dragSnapshot.isDragging ? `${transform || ''} rotate(2deg) scale(1.02)`.trim() : transform,
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  left: 'auto',
                  right: 'auto'
                }}
                sx={{
                  cursor: dragSnapshot.isDragging ? 'grabbing' : 'grab',
                  opacity: dragSnapshot.isDragging ? 0.8 : 1,
                  mb: { xs: 1.5, md: 1 }
                }}
              >
                <LeadCells lead={lead} priorityMeta={priorityMeta} dragSnapshot={dragSnapshot} sourceOptions={sourceOptions} />
              </Box>
            );
          }}
        </Draggable>
      );
    }

    return (
      <Box
        key={lead._id}
        onClick={onRowClick}
        sx={{
          cursor: 'pointer',
          mb: { xs: 1.5, md: 1 }
        }}
      >
        <LeadCells lead={lead} priorityMeta={priorityMeta} dragSnapshot={null} sourceOptions={sourceOptions} />
      </Box>
    );
  };

  if (groupBy !== CRM_GROUP_BY.STAGE) {
    return (
      <Stack spacing={3}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Drag and drop to change stage is only available when grouping by stage.
        </Typography>
        {alternateSections.map((section) => (
          <Box key={section.key}>
            <Box
              sx={{
                pb: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1" fontWeight="bold">
                  {section.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {section.leads?.length || 0} leads
                </Typography>
              </Stack>
            </Box>

            <Box
              sx={{
                position: 'relative',
                minWidth: { xs: '100%', md: 760 },
                overflow: 'hidden',
                backgroundColor: { xs: 'transparent', md: '#fff' },
                border: { xs: 'none', md: '1px solid' },
                borderColor: 'grey.200',
                borderRadius: { xs: 0, md: 1 }
              }}
            >
              {tableHeader}
              <Box sx={{ px: { xs: 0, md: 1 }, py: { xs: 0, md: 0 } }}>
                {(section.leads || []).map((lead) => renderLeadBlock(lead, { draggable: false }))}
              </Box>
            </Box>
          </Box>
        ))}
      </Stack>
    );
  }

  const showKanban = layout === CRM_LAYOUT.KANBAN && groupBy === CRM_GROUP_BY.STAGE;

  if (showKanban) {
    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            gap: 2,
            overflowX: 'auto',
            overflowY: 'hidden',
            pb: 1,
            width: '100%',
            maxWidth: '100%',
            minHeight: { xs: 'min(480px, 85dvh)', md: KANBAN_BOARD_HEIGHT },
            height: { xs: 'min(480px, 85dvh)', md: KANBAN_BOARD_HEIGHT }
          }}
        >
          {orderedStages.map((stage) => (
            <Box
              key={stage.slug}
              sx={{
                flex: '0 0 min(300px, 85vw)',
                minWidth: 260,
                maxWidth: 340,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                maxHeight: '100%',
                borderRadius: 0.5,
                border: '1px solid',
                borderColor: 'grey.200',
                bgcolor: 'grey.50'
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  flexShrink: 0,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  bgcolor: 'background.paper',
                  borderTopLeftRadius: '4px',
                  borderTopRightRadius: '4px'
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight="bold" noWrap>
                    {stage.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {groupedByStage[stage.slug]?.length || 0}
                  </Typography>
                </Stack>
                {!stage.isLocked && onEditStage && (
                  <IconButton size="small" onClick={() => onEditStage(stage)} aria-label="Edit stage">
                    <PencilSimple size={16} />
                  </IconButton>
                )}
              </Box>
              <Droppable droppableId={stage.slug} type="lead">
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      flex: 1,
                      minHeight: 120,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      p: 1,
                      transition: 'background-color 0.2s',
                      bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                      borderBottomLeftRadius: '4px',
                      borderBottomRightRadius: '4px'
                    }}
                  >
                    {(groupedByStage[stage.slug] || []).map((lead, index) => {
                      const priorityMeta = getPriorityChip(lead.priority);
                      return (
                        <Draggable key={lead._id} draggableId={lead._id} index={index}>
                          {(dragProvided, dragSnapshot) => {
                            const dragStyle = dragProvided.draggableProps.style || {};
                            const transform = dragStyle.transform;
                            const isDragging = dragSnapshot.isDragging;
                            return (
                              <Box
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                onClick={() => onLeadClick?.(lead)}
                                style={{
                                  ...dragStyle,
                                  transform: isDragging
                                    ? `${transform || ''} rotate(2deg) scale(1.02)`.trim()
                                    : transform,
                                  zIndex: isDragging ? 2000 : dragStyle.zIndex,
                                  ...(isDragging
                                    ? {
                                        width: 'min(284px, calc(85vw - 48px))',
                                        maxWidth: 'min(300px, 85vw)',
                                        boxSizing: 'border-box'
                                      }
                                    : {
                                        width: '100%',
                                        maxWidth: '100%',
                                        boxSizing: 'border-box',
                                        left: 'auto',
                                        right: 'auto'
                                      })
                                }}
                                sx={{
                                  cursor: dragSnapshot.isDragging ? 'grabbing' : 'grab',
                                  opacity: dragSnapshot.isDragging ? 0.8 : 1,
                                  mb: 1.5
                                }}
                              >
                                <KanbanLeadCard lead={lead} priorityMeta={priorityMeta} dragSnapshot={dragSnapshot} />
                              </Box>
                            );
                          }}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </Box>
          ))}
        </Box>
      </DragDropContext>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Stack spacing={3}>
        {orderedStages.map((stage) => {
          const collapsed = Boolean(collapsedStages[stage.slug]);
          return (
            <Box key={stage.slug}>
              <Box
                role="button"
                tabIndex={0}
                aria-expanded={!collapsed}
                aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${stage.name}`}
                onClick={() => toggleStageCollapsed(stage.slug)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleStageCollapsed(stage.slug);
                  }
                }}
                sx={{
                  pb: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  borderRadius: 1,
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: 2
                  }
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <Box
                    component="span"
                    sx={{ display: 'inline-flex', color: 'text.secondary', flexShrink: 0, lineHeight: 0 }}
                    aria-hidden
                  >
                    {collapsed ? <CaretRight size={18} weight="bold" /> : <CaretDown size={18} weight="bold" />}
                  </Box>
                  <Typography variant="subtitle1" fontWeight="bold" noWrap>
                    {stage.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {groupedByStage[stage.slug]?.length || 0} leads
                  </Typography>
                </Stack>
              </Box>

              <Collapse in={!collapsed} timeout="auto" unmountOnExit={false}>
                <Droppable droppableId={stage.slug} type="lead">
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        position: 'relative',
                        minWidth: { xs: '100%', md: 760 },
                        overflow: 'hidden',
                        transition: 'all 0.2s ease',
                        backgroundColor: { xs: 'transparent', md: snapshot.isDraggingOver ? 'grey.50' : '#fff' },
                        border: {
                          xs: 'none',
                          md: `${snapshot.isDraggingOver ? 2 : 1}px solid ${snapshot.isDraggingOver ? 'primary.main' : 'grey.200'}`
                        },
                        borderRadius: { xs: 0, md: 1 }
                      }}
                    >
                      {tableHeader}
                      <Box sx={{ px: { xs: 0, md: 1 }, py: { xs: 0, md: 0 } }}>
                        {(groupedByStage[stage.slug] || []).map((lead, index) =>
                          renderLeadBlock(lead, { draggable: true, index })
                        )}
                        {provided.placeholder}
                      </Box>
                    </Box>
                  )}
                </Droppable>
              </Collapse>
            </Box>
          );
        })}
      </Stack>
    </DragDropContext>
  );
};

export default CRMListView;
